import {
  RequestQuote,
  Email,
  Add as AddIcon,
  ArrowDownward,
  ArrowUpward,
  Groups,
  Percent,
  Speed,
  TrendingUp,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useState } from "react";
import { useHasPermission } from "../../contexts/auth";
import { SettlementCreator } from "../../components/settlements/settlementCreator/settlementCreator.component";
import { SendReminder } from "../../components/settlements/sendReminder/sendReminder.component";
import EquipmentCounterForm from "../../components/ui/equipmentCounterForm/equipmentCounterForm.component";
import {
  setSaleSummaryViewOption,
  useSaleSummaryViewOptions,
} from "../../hooks/useSaleSummaryViewOptions";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";

export const SettlementsActions = () => {
  const { t } = useTranslation();
  const [generateSettlementOpened, setGenerateSettlementOpened] = useState<boolean>(false);
  const [sendReminderOpened, setSendReminderOpened] = useState<boolean>(false);
  const [addCounterOpened, setAddCounterOpened] = useState<boolean>(false);
  const canAddSettlement = useHasPermission("settlement.add_settlement");
  const canChangeSettlement = useHasPermission("settlement.change_settlement");
  const canAddCounter = useHasPermission("settlement.add_equipmentcounter");
  const canViewSaleSummary = useHasPermission("settlement.view_sale_summary");
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  const viewOptions = useSaleSummaryViewOptions();

  const actions: Action[] = [
    {
      icon: <RequestQuote />,
      label: t("Settlement Creator"),
      onClick: () => { setGenerateSettlementOpened(true) },
      disabled: !canAddSettlement
    },
    {
      icon: <Email />,
      label: t("Send reminder"),
      onClick: () => { setSendReminderOpened(true) },
      disabled: !canChangeSettlement
    },
    {
      id: "add-new-counter",
      icon: <AddIcon />,
      label: t("Add new counter"),
      onClick: () => { setAddCounterOpened(true); },
      disabled: !canAddCounter,
    },
  ];

  const highlightWhen = (active: boolean) => (active ? "var(--primaryColor)" : undefined);
  const newestFirst = viewOptions.contractNumberSort === "desc";

  // How the list is ordered and what it shows - here rather than above the
  // list, so they stay reachable from the middle of a long month.
  const displayOptionActions: Action[] = [
    {
      // One button flipping between the two orders; the arrow shows which way.
      icon: newestFirst ? <ArrowDownward /> : <ArrowUpward />,
      label: newestFirst ? t("Newest number first") : t("Oldest number first"),
      onClick: () =>
        setSaleSummaryViewOption("contractNumberSort", newestFirst ? "asc" : "desc"),
    },
    {
      icon: <Groups />,
      label: t("Group by client"),
      onClick: () => setSaleSummaryViewOption("groupByClient", !viewOptions.groupByClient),
      color: highlightWhen(viewOptions.groupByClient),
    },
    {
      icon: <Speed />,
      label: t("Counter readings"),
      onClick: () => setSaleSummaryViewOption("counterReadings", !viewOptions.counterReadings),
      color: highlightWhen(viewOptions.counterReadings),
    },
    ...(canViewProfitability
      ? [
          {
            icon: <TrendingUp />,
            label: t("Profitability"),
            onClick: () => setSaleSummaryViewOption("profitability", !viewOptions.profitability),
            color: highlightWhen(viewOptions.profitability),
          },
        ]
      : []),
    {
      icon: <Percent />,
      label: t("Inflation"),
      onClick: () => setSaleSummaryViewOption("inflation", !viewOptions.inflation),
      color: highlightWhen(viewOptions.inflation),
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0 }}>
        {/* The list shows no settlements without this permission. */}
        {canViewSaleSummary ? (
          <Box component="fieldset" sx={serviceActionsSectionStyle}>
            <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
              {t("Display options")}
            </Typography>
            <Toolbar actions={displayOptionActions} noTopPadding showShortcut={false} />
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Toolbar actions={actions} noTopPadding={canViewSaleSummary} />
        </Box>
      </Box>
      <CustomModal
        opened={generateSettlementOpened}
        setOpened={setGenerateSettlementOpened}
        title={t("Settlement Creator")}
        content={
          <SettlementCreator
            onSuccess={() => setGenerateSettlementOpened(false)}
          />
        }
        width="min(1040px, 94vw)"
      />
      <CustomModal
        opened={sendReminderOpened}
        setOpened={setSendReminderOpened}
        title={t("Send reminder")}
        content={
          <SendReminder
            onSuccess={() => {
              setSendReminderOpened(false);
              window.dispatchEvent(new Event("settlementUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={addCounterOpened}
        setOpened={setAddCounterOpened}
        title={t("Add new counter")}
        content={
          <EquipmentCounterForm
            onSuccess={() => {
              setAddCounterOpened(false);
              window.dispatchEvent(new Event("equipmentCounterAdded"));
            }}
            hiddenFields={["job"]}
          />
        }
      />
    </>
  );
};
